import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";
import HasRight from "../HasRight";
import LinkTr from "../LinkTr";

import { V2 } from "../../Api";
import { formatName } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";


interface IMetricsBonusViewState {
    isLoading: boolean | string;

    group: string;
    month: string;

    bonuses: any[];

    [name: string]: any;
}

class MetricsBonusView extends React.Component<IAuthProps & RouteComponentPropsHistory<any, any>, IMetricsBonusViewState> {
    constructor(props: Readonly<IAuthProps & RouteComponentPropsHistory<any, any>>) {
        super(props);

        const today = DateTime.local();

        let group = (props.location.state && props.location.state.group);
        if (!group) {
            if (props.authContext.hasRight("metrics:bonus:m:read")) {
                group = "0";
            }
            else if (props.authContext.hasRight("metrics:bonus:n:read")) {
                group = "1";
            }
            else if (props.authContext.hasRight("metrics:bonus:d:read")) {
                group = "2";
            }
            else if (props.authContext.hasRight("metrics:bonus:so:read")) {
                group = "3";
            }
            else if (props.authContext.hasRight("metrics:bonus:mm:read")) {
                group = "4";
            }
        }

        this.state = {
            isLoading: true,

            group,
            month: (props.location.state && props.location.state.month) || today.toFormat("yyyy-MM"),

            bonuses: []
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.search = this.search.bind(this);
    }

    public componentDidMount() {
        this.search();
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <DelayInput type="month" name="month" value={this.state.month} className="fill-space" timeout={300} onChangeDelayed={this.handleElementChange} />
                    <select name="group" value={this.state.group} onChange={this.handleElementChange}>
                        <HasRight right="metrics:bonus:m:read">
                            <option value="0">HF Team</option>
                            <option value="5">Telemarketing</option>
                            <option value="6">Surgical Consultants</option>
                        </HasRight>
                        <HasRight right="metrics:bonus:n:read">
                            <option value="1">NP</option>
                        </HasRight>
                        <HasRight right="metrics:bonus:d:read">
                            <option value="2">Doctors</option>
                        </HasRight>
                        <HasRight right="metrics:bonus:so:read">
                            <option value="3">Surgical Outcomes</option>
                        </HasRight>
                        <HasRight right="metrics:bonus:mm:read">
                            <option value="4">Marketing Manager</option>
                        </HasRight>
                    </select>
                </div>
                {
                    this.state.isLoading || this.state.bonuses.length > 0
                        ? <table className="table-vertical">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className="col-smallest">Cases</th>
                                    {
                                        this.state.group === "2"
                                            ? <th className="col-smallest col-right">Primary Total</th>
                                            : <th className="col-smallest col-right">Total</th>
                                    }
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Employees</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.isLoading ?
                                        <tr>
                                            <td colSpan={10}>
                                                {
                                                    typeof this.state.isLoading === "string"
                                                        ? <span className="error-text">{this.state.isLoading}</span>
                                                        : <div className="spinner-center lds-dual-ring" />
                                                }
                                            </td>
                                        </tr>
                                        : this.state.bonuses.map(a => (
                                            <LinkTr key={a.id} to={`/metrics/bonus/${a.id}`} className="clickable">
                                                <td data-caption="Name: " className="inline">{formatName(a.employee.name)}</td>
                                                <td data-caption="Cases: " className="col-right inline">{a.caseCount}</td>
                                                {
                                                    this.state.group === "2"
                                                        ? <td data-caption="Primary Total: " className="col-right col-bold inline">{a.total} <small>wRVU</small></td>
                                                        : <td data-caption="Total: " className="col-right col-bold inline">{(+a.total).toLocaleString(undefined, { style: "currency", currency: "USD" })}</td>
                                                }
                                            </LinkTr>
                                        ))
                                }
                            </tbody>
                        </table>
                        : <div>
                            <h2>No bonuses calculated</h2>
                            <span>No bonuses have been calculated for this month.</span>
                            <br />
                            <br />
                            <HasRight right={this.groupToRightCalc(this.state.group)}>
                                <div className="action-panel-dialog">
                                    <Link to={{ pathname: "/metrics/bonus/calculate", state: { group: this.state.group, month: this.state.month } }} className="button text-with-icon"><span>Calculate bonuses</span><i className="icon-chevron-right" /></Link>
                                </div>
                            </HasRight>
                        </div>
                }
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            group: this.state.group,
            month: this.state.month,
        };
        this.props.history.replace(currentLocation);
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const bonuses = await V2.get<any[]>(`bonus/month/${this.state.month}?group=${this.state.group}`);

            bonuses.sort((a, b) => formatName(a.employee.name).localeCompare(formatName(b.employee.name)))

            this.setState({
                isLoading: false,

                bonuses,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            })
        }
    }

    private groupToRightCalc(group: string) {
        switch (group) {
            case "0": return "metrics:bonus:m:calc";
            case "5": return "metrics:bonus:m:calc";
            case "6": return "metrics:bonus:m:calc";
            case "1": return "metrics:bonus:n:calc";
            case "2": return "metrics:bonus:d:calc";
            case "3": return "metrics:bonus:so:calc";
            case "4": return "metrics:bonus:mm:calc";
            default: return "right:invalid";
        }
    }
}

export default withTitle("Employee Bonusing", withAuth(MetricsBonusView));
